import {
  DimeCard,
  DimeCardAction,
  DimeCardDescription,
  DimeCardHeader,
  DimeCardTitle,
  DimeDataTable,
} from '@nudool/design-system';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { TicketIcon } from 'lucide-react';
import { useMemo } from 'react';

import { ListEmptyState } from '../../components/ListEmptyState.js';
import { ListErrorState } from '../../components/ListErrorState.js';
import {
  parseListRouteSearch,
  type ListRouteSearch,
} from '../../hooks/use-list-table-helpers/index.js';
import { buildListEmptyMessage } from '../../lib/build-list-empty-message.js';
import { usePermissions } from '../../modules/iam/permissions-context.js';
import { isReversibleExpiredCredit } from '../../modules/promotion-credits/is-reversible-expired-credit.js';
import { IssueCreditAction } from '../../modules/promotion-credits/issue-credit-action.js';
import { PromotionCreditBrandFilter } from '../../modules/promotion-credits/promotion-credit-brand-filter.js';
import { makePromotionCreditColumns } from '../../modules/promotion-credits/promotion-credit-columns.js';
import { PromotionCreditCustomerFilter } from '../../modules/promotion-credits/promotion-credit-customer-filter.js';
import { PromotionCreditFiltersMenu } from '../../modules/promotion-credits/promotion-credit-filters-menu.js';
import { PromotionCreditPageTotals } from '../../modules/promotion-credits/promotion-credit-page-totals.js';
import { ReverseExpiredCreditsAction } from '../../modules/promotion-credits/reverse-expired-credits-action.js';
import { usePromotionCreditList } from '../../modules/promotion-credits/use-promotion-credit-list.js';

export const Route = createFileRoute('/credits/')({
  validateSearch: (search: Record<string, unknown>): ListRouteSearch =>
    parseListRouteSearch(search),
  component: CreditsPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function CreditsPage() {
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const canReverse = permissions.includes('promotion:delete');
  const list = usePromotionCreditList();
  const { tableProps, selection } = list;
  const columns = useMemo(() => makePromotionCreditColumns(), []);

  // Only a view no filter or search could widen may show the full-page empty state, since it also hides the filters.
  const isUnfilteredView = !list.search && list.filterCount === 0;

  return (
    <DimeCard borderless className="gap-0 py-0">
      <DimeCardHeader className="px-0 mb-4">
        <DimeCardTitle className="text-base flex items-center gap-2">Credits</DimeCardTitle>
        <DimeCardDescription className="text-sm">
          Review credits issued to customers, what is still available and when it expires.
        </DimeCardDescription>
        <DimeCardAction>
          <IssueCreditAction onIssued={list.reloadAfterIssue} />
        </DimeCardAction>
      </DimeCardHeader>
      {list.loadFailure ? (
        <ListErrorState
          title="Could not load credits"
          message={list.loadFailure.message}
          onRetry={() => void list.refetch()}
        />
      ) : !list.loading && list.rows.length === 0 && isUnfilteredView ? (
        <ListEmptyState
          icon={<TicketIcon />}
          title="No credits yet"
          description="Credits issued to customers will appear here."
          action={<IssueCreditAction onIssued={list.reloadAfterIssue} />}
        />
      ) : (
        <>
          <DimeDataTable
            columns={columns}
            data={list.rows}
            maxBodyHeight="viewport"
            searchColumn="creditMemoNumber"
            searchPlaceholder="Search by credit #, customer or details…"
            filters={
              <>
                <PromotionCreditCustomerFilter value={list.customer} onChange={list.setCustomer} />
                {list.brands.length > 1 || list.filters.brandIds.length > 0 ? (
                  <PromotionCreditBrandFilter
                    brands={list.brands}
                    brandIds={list.filters.brandIds}
                    onBrandIdsChange={(brandIds) => list.updateChoices({ brandIds })}
                  />
                ) : null}
                <PromotionCreditFiltersMenu
                  filters={list.filters}
                  types={list.types}
                  count={list.menuFilterCount}
                  onChange={list.updateChoices}
                  onClearAll={list.clearAll}
                />
              </>
            }
            actions={
              <ReverseExpiredCreditsAction
                isExpiredFilterActive={list.isExpiredFilterActive}
                credits={selection.reversibleCredits}
                onReversed={list.reloadAfterReverse}
              />
            }
            showViewOptions
            showSelect={list.isExpiredFilterActive && canReverse}
            canSelectRow={isReversibleExpiredCredit}
            selected={selection.selected}
            onUpdateSelected={selection.onUpdateSelected}
            emptyMessage={buildListEmptyMessage('credits', list.search, list.filterCount > 0)}
            onRowClick={(credit) => {
              if (!credit.id) return;
              void navigate({ to: '/credits/$creditId', params: { creditId: credit.id } });
            }}
            loadingRowCount={tableProps.pageSize}
            exportFileName="credits"
            {...tableProps}
          />
          <PromotionCreditPageTotals credits={list.rows} />
        </>
      )}
    </DimeCard>
  );
}
